import { ApplicationDataNotice } from "./ApplicationDataNotice";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { TextInput } from "@/components/design/TextField";
import { publicClient } from "@/graphql/public-client";
import { SubmitPublicListingApplicationDocument } from "@/graphql/generated/public";
import { ingestFiles } from "@/lib/file-ingest";
import { MAX_DOCUMENT_UPLOAD_BYTES, MAX_PUBLIC_APPLICATION_TOTAL_BYTES, PROJECT_DOCUMENT_ACCEPT } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import {
  admitApplicationDocuments,
  NO_APPLICATION_DOCUMENTS,
  publicApplicationDocumentsInput,
  type ApplicationDocumentProblem,
  type ApplicationDocuments,
  type DocumentSlotKind,
} from "./application-documents";
import { ApplicationDocumentsSection } from "./ApplicationDocumentsSection";
import { ContactTextField } from "./ContactTextField";
import { MailLanguageField, type MailLanguageChoice } from "./MailLanguageField";
import {
  classifySubmission,
  EMPTY_PUBLIC_CONTACT,
  missingPublicFields,
  publicApplicationInput,
  publicContactErrors,
  type PublicContactField,
  type PublicContactValues,
  type PublicSubmission,
} from "./public-application";

/** Was nach dem Absenden zu sagen ist; null solange das Formular steht. */
type Outcome = PublicSubmission | "refused";

// Bewerbung ohne Konto (@external-adr GENESIS-0072-applying-without-an-account-confirms-email-before-a-person-exists,
// @external-adr GENESIS-0083-a-company-applies-without-an-account-through-its-proven-contact). Dieselben Bausteine wie der
// Dialog mit Konto (Kontaktfelder, Dokumente, Sprache), aber alles frei und ohne Profil: genesis kennt die Person erst,
// wenn sie die E-Mail bestätigt hat. Sucht die Ausschreibung Unternehmen, bewirbt sich ein Unternehmen, die Person ist
// seine Kontaktperson; der Typ wird nie aus einem Feld erraten.
//
// Die Antwort ist absichtlich immer gleich: sie verrät nicht, ob es die Adresse, ein Konto oder eine Bewerbung schon
// gibt. Deshalb bittet der Dialog danach nur, im Postfach fortzufahren; ob ein Konto zur Adresse gehört, erfährt erst,
// wer den Link aus der Mail öffnet. Die Anmeldung steht jedem offen, unabhängig von der Adresse. Kam keine lesbare
// Antwort, ist offen, ob die Einreichung ankam: dann wird nichts automatisch wiederholt, der Dialog bittet erst ins
// Postfach zu schauen.
export function PublicApplyDialog({
  listingId,
  listingName,
  company,
  loginHref,
  onClose,
}: {
  listingId: string;
  listingName: string;
  company: boolean;
  /** The login that returns to this listing, where the application with an account continues as before. */
  loginHref: string;
  onClose: () => void;
}) {
  const intl = useIntl();
  const [values, setValues] = useState<PublicContactValues>(EMPTY_PUBLIC_CONTACT);
  const [companyName, setCompanyName] = useState("");
  const [documents, setDocuments] = useState<ApplicationDocuments>(NO_APPLICATION_DOCUMENTS);
  const [problems, setProblems] = useState<ApplicationDocumentProblem[]>([]);
  const [language, setLanguage] = useState<MailLanguageChoice>("");
  const [consented, setConsented] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [reading, setReading] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const errors = publicContactErrors(values);
  const missing = missingPublicFields(values);
  const companyInvalid = company && (!companyName.trim() || companyName.trim().length > 255);
  const busy = reading || pending;
  const invalid = Object.keys(errors).length > 0 || missing.length > 0 || companyInvalid || !consented;

  function close() {
    if (!pending) onClose();
  }

  async function addFiles(slot: DocumentSlotKind, files: File[]) {
    if (busy || files.length === 0) return;
    setReading(true);
    try {
      const ingested = await ingestFiles(files, { maxBytes: MAX_DOCUMENT_UPLOAD_BYTES, accept: PROJECT_DOCUMENT_ACCEPT });
      const admitted = admitApplicationDocuments(documents, slot, ingested, MAX_PUBLIC_APPLICATION_TOTAL_BYTES);
      setDocuments(admitted.next);
      setProblems(admitted.problems);
    } catch (e) {
      setProblems([]);
      setError(userErrorMessage(intl, e));
    } finally {
      setReading(false);
    }
  }

  async function submit() {
    // Only a refusal or a busy server may be followed by another attempt: both say nothing was taken.
    if (busy || outcome === "answered" || outcome === "unknown") return;
    setReveal(true);
    setError(null);
    setOutcome(null);
    if (invalid) return;
    setPending(true);
    try {
      await publicClient.mutate({
        mutation: SubmitPublicListingApplicationDocument,
        variables: {
          input: publicApplicationInput({
            listingId,
            company,
            companyName,
            contact: values,
            language,
            documents: publicApplicationDocumentsInput(documents, company),
          }),
        },
      });
      setOutcome("answered");
    } catch (e) {
      // A GraphQL error means the server read the request and executed nothing (a schema refusal); an HTTP status
      // says what the BFF did; no status at all means the outcome is unknown.
      if (CombinedGraphQLErrors.is(e)) {
        setOutcome("refused");
      } else {
        const failure = e as { statusCode?: number; response?: { status?: number; headers?: Headers } };
        const status = failure.statusCode ?? failure.response?.status ?? null;
        setOutcome(status === 400 || status === 403 || status === 413
          ? "refused" : classifySubmission(status, failure.response?.headers));
      }
    } finally {
      setPending(false);
    }
  }

  const accountLogin = (
    <p className="smycles-field-meta text-ink-muted">
      <FormattedMessage
        id="share.public.apply.accountHint"
        defaultMessage="Bereits ein Konto? {login}."
        values={{
          login: (
            <a id="public-apply-login" href={loginHref} className="underline underline-offset-2 hover:text-ink">
              <FormattedMessage id="share.public.apply.accountLogin" defaultMessage="Anmelden und bewerben" />
            </a>
          ),
        }}
      />
    </p>
  );

  const title = (
    <FormattedMessage
      id="share.applications.dialog.title"
      defaultMessage="Bewerbung auf {name}"
      values={{ name: listingName || intl.formatMessage({ id: "share.listings.untitled", defaultMessage: "Ohne Titel" }) }}
    />
  );

  if (outcome === "answered" || outcome === "unknown") {
    return (
      <Modal
        id="public-apply-dialog"
        open
        onClose={onClose}
        maxWidth="40rem"
        title={title}
        footer={
          <Button id="public-apply-close" onClick={onClose}>
            <FormattedMessage id="common.close" defaultMessage="Schließen" />
          </Button>
        }
      >
        <p id="public-apply-outcome" role="status" className="text-smycles-body text-ink">
          {outcome === "answered" ? (
            <FormattedMessage
              id="share.public.apply.sent"
              defaultMessage="Bitte prüfe dein E-Mail-Postfach, um fortzufahren."
            />
          ) : (
            <FormattedMessage
              id="share.public.apply.unknown"
              defaultMessage="Wir wissen nicht, ob deine Bewerbung angekommen ist. Sieh bitte zuerst in deinem Postfach nach einer Bestätigungsmail und sende sie erst erneut, wenn keine kommt."
            />
          )}
        </p>
        <p className="smycles-field-meta mt-2 text-ink-muted">
          <FormattedMessage
            id="share.public.apply.sentHint"
            defaultMessage="Der Link gilt 72 Stunden. Kommt keine Mail, prüfe den Spam-Ordner."
          />
        </p>
        <div className="mt-4">{accountLogin}</div>
      </Modal>
    );
  }

  return (
    <Modal
      id="public-apply-dialog"
      open
      onClose={close}
      maxWidth="40rem"
      title={title}
      footer={
        <>
          <Button variant="secondary" disabled={pending} onClick={close}>
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </Button>
          <Button id="public-apply-submit" disabled={busy} onClick={() => void submit()}>
            {pending ? (
              <FormattedMessage id="share.applications.dialog.sending" defaultMessage="Wird gesendet…" />
            ) : (
              <FormattedMessage id="share.applications.dialog.submit" defaultMessage="Bewerbung absenden" />
            )}
          </Button>
        </>
      }
    >
      {accountLogin}

      {company ? (
        <section className="mt-6">
          <h3 className="text-smycles-body font-medium">
            <FormattedMessage id="share.public.apply.companyTitle" defaultMessage="Unternehmen" />
          </h3>
          <p className="smycles-field-meta mt-1 text-ink-muted">
            <FormattedMessage
              id="share.public.apply.companyHint"
              defaultMessage="Diese Ausschreibung sucht Unternehmen. Es bewirbt sich das Unternehmen, du bist seine Kontaktperson."
            />
          </p>
          <label className="mt-3 block">
            <span className="smycles-field-label">
              <FormattedMessage id="share.applications.company.name" defaultMessage="Unternehmensname" />
            </span>
            <TextInput id="public-apply-company-name" className="mt-1" value={companyName} maxLength={255} disabled={busy}
              aria-invalid={(reveal && companyInvalid) || undefined} onChange={(event) => setCompanyName(event.target.value)} />
          </label>
          {reveal && companyInvalid ? (
            <p role="alert" className="smycles-field-meta mt-1 text-ink">
              <FormattedMessage id="share.public.apply.companyRequired" defaultMessage="Bitte gib den Namen des Unternehmens an." />
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="mt-6">
        <h3 className="text-smycles-body font-medium">
          {company ? (
            <FormattedMessage id="share.public.apply.contactPerson" defaultMessage="Kontaktperson" />
          ) : (
            <FormattedMessage id="share.applications.contact.title" defaultMessage="Kontaktdaten" />
          )}
        </h3>
        <p className="smycles-field-meta mt-1 text-ink-muted">
          <FormattedMessage
            id="share.public.apply.contactHint"
            defaultMessage="An diese E-Mail-Adresse schicken wir den Link zur Bestätigung. Ein Konto entsteht dabei nicht."
          />
        </p>
        <div className="mt-3 grid grid-cols-1 gap-4 @sm:grid-cols-2">
          {field("givenName", <FormattedMessage id="share.applications.contact.givenName" defaultMessage="Vorname" />, 128)}
          {field("familyName", <FormattedMessage id="share.applications.contact.familyName" defaultMessage="Nachname" />, 128)}
          {field("email", <FormattedMessage id="share.public.apply.email" defaultMessage="E-Mail" />, 254, "email", "email")}
          {field("phone", <FormattedMessage id="profile.page.label.phone" defaultMessage="Phone" />, undefined, "tel", "tel")}
          {field("website", <FormattedMessage id="profile.page.label.website" defaultMessage="Website" />, undefined, "url", "url")}
          {field("street", <FormattedMessage id="profile.page.label.street" defaultMessage="Street" />)}
          {field("houseNumber", <FormattedMessage id="profile.page.label.houseNumber" defaultMessage="House number" />)}
          {field("zipCode", <FormattedMessage id="profile.page.label.zipCode" defaultMessage="Postal code" />)}
          {field("city", <FormattedMessage id="profile.page.label.city" defaultMessage="Place of residence" />)}
        </div>
        {reveal && missing.length ? (
          <p id="public-apply-missing" role="alert" className="smycles-field-meta mt-2 text-ink">
            <FormattedMessage id="share.public.apply.required" defaultMessage="Bitte gib mindestens Vorname und E-Mail an." />
          </p>
        ) : null}
      </section>

      <ApplicationDocumentsSection
        idPrefix="public-apply"
        documents={documents}
        problems={problems}
        company={company}
        totalLimit={MAX_PUBLIC_APPLICATION_TOTAL_BYTES}
        disabled={busy}
        onFiles={(slot, files) => void addFiles(slot, files)}
        onRemoveCv={() => {
          setProblems([]);
          setDocuments((current) => ({ ...current, cv: null }));
        }}
        onRemoveOther={(index) => {
          setProblems([]);
          setDocuments((current) => ({ ...current, others: current.others.filter((_, i) => i !== index) }));
        }}
      />

      <section className="mt-6 space-y-4">
        <MailLanguageField id="public-apply-mail-language" value={language} disabled={busy} onChange={setLanguage} />
        <ApplicationDataNotice company={company} />
        <label className="flex items-start gap-2 text-smycles-body">
          <input id="public-apply-consent" type="checkbox" checked={consented} disabled={busy}
            onChange={(event) => setConsented(event.target.checked)}
            className="mt-1 accent-ink focus-visible:ring-2 focus-visible:ring-ink/30" />
          <span>
            <FormattedMessage
              id="share.public.apply.consent"
              defaultMessage="Ich willige ein, dass meine Angaben und Unterlagen wie oben beschrieben gespeichert und verarbeitet werden. Erst nach der Bestätigung per E-Mail werden sie Teil einer Bewerbung; unbestätigte Angaben werden gelöscht."
            />
          </span>
        </label>
        {reveal && !consented ? (
          <p role="alert" className="smycles-field-meta text-ink">
            <FormattedMessage id="share.public.apply.consentRequired" defaultMessage="Ohne Einwilligung können wir die Bewerbung nicht annehmen." />
          </p>
        ) : null}
      </section>

      {outcome === "busy" || outcome === "refused" || error ? (
        <p id="public-apply-error" role="alert" className="mt-4 text-smycles-meta text-ink-accent">
          {error ?? (outcome === "busy" ? (
            <FormattedMessage
              id="share.public.apply.busy"
              defaultMessage="Gerade gehen sehr viele Bewerbungen ein. Deine wurde nicht angenommen; bitte versuche es in einigen Minuten noch einmal."
            />
          ) : (
            <FormattedMessage
              id="share.public.apply.refused"
              defaultMessage="Die Bewerbung wurde nicht angenommen. Bitte prüfe deine Angaben und lade die Seite neu, bevor du es noch einmal versuchst."
            />
          ))}
        </p>
      ) : null}
    </Modal>
  );

  function field(name: PublicContactField, label: ReactNode, maxLength?: number,
                 type?: "text" | "email" | "tel" | "url", autoComplete?: string) {
    return (
      <ContactTextField
        key={name}
        idPrefix="public-apply"
        field={name}
        label={label}
        value={values[name]}
        disabled={busy}
        error={errors[name] ?? null}
        reveal={reveal}
        type={type}
        autoComplete={autoComplete}
        maxLength={maxLength}
        onChange={(next) => setValues((current) => ({ ...current, [name]: next }))}
      />
    );
  }
}
